import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Card, CardContent, Typography, Box, Modal, TextField, IconButton, InputAdornment, List, ListItem, ListItemText, ListItemAvatar, Avatar, Chip, Divider } from '@mui/material';
import { Tabs, Tab } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
// Removed socket.io-client import - using native WebSocket
import Confetti from 'react-confetti';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Parser from 'html-react-parser';

// Persist and reuse a unique browser identifier
const COOKIE_ID_KEY = 'cookie_id';
function getOrCreateCookieId() {
  try {
    // Reuse any existing id (supports legacy key if present)
    let existingId = localStorage.getItem(COOKIE_ID_KEY) || localStorage.getItem('cookie_user_id');
    if (existingId && typeof existingId === 'string' && existingId.trim() !== '') {
      // Normalize to the primary key for consistency going forward
      localStorage.setItem(COOKIE_ID_KEY, existingId);
      return existingId;
    }
    const newId = `browser_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(COOKIE_ID_KEY, newId);
    return newId;
  } catch (e) {
    // Fallback if localStorage is unavailable
    return `browser_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
}

// Helper function to extract YouTube video ID from various URL formats
function getYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  
  const trimmedUrl = url.trim();
  if (trimmedUrl === '') return null;
  
  // Match various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
  ];
  
  for (const pattern of patterns) {
    const match = trimmedUrl.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
}

// Player Profile Modal Component
const PlayerProfileModal = ({ open, onClose, player }) => {
  if (!player) return null;

  // Helper function to check if a value is valid (not null, undefined, empty, or whitespace)
  const hasValue = (value) => {
    return value !== null && value !== undefined && String(value).trim() !== '';
  };

  // Helper function to check if is_icon is true (handles boolean, string "1", "true", etc.)
  const isIconPlayer = (isIcon) => {
    if (isIcon === null || isIcon === undefined) return false;
    if (typeof isIcon === 'boolean') return isIcon === true;
    if (typeof isIcon === 'string') {
      const trimmed = isIcon.trim().toLowerCase();
      return trimmed === '1' || trimmed === 'true' || trimmed === 'yes';
    }
    return Boolean(isIcon);
  };

  const formatSlNo = (slNo) => {
    if (!slNo) return '#000';
    const num = String(slNo).padStart(3, '0');
    return `#${num}`;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="player-profile-modal"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
          }
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          width: '100%',
          maxWidth: { xs: '90vw', sm: '520px', md: '560px' },
          maxHeight: '90vh',
          overflow: 'auto',
          outline: 'none',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Player ID Badge */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '4px 12px',
            borderRadius: '12px',
            fontWeight: 700,
            fontSize: '14px',
            color: '#333',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          {formatSlNo(player.sl_no)}
        </Box>

        {/* Icon Badge */}
        {isIconPlayer(player.is_icon) && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 56,
              zIndex: 10,
              backgroundColor: '#ffc107',
              color: '#000',
              padding: '4px 12px',
              borderRadius: '20px',
              fontWeight: 700,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: '16px' }} />
            ICON
          </Box>
        )}

        {/* Content */}
        <Box sx={{ p: 4, pt: 6 }}>
          {/* Profile Picture */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: { xs: '220px', sm: '280px' },
                height: { xs: '220px', sm: '280px' },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '240px', sm: '300px' },
                  height: { xs: '240px', sm: '300px' },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  opacity: 0.2,
                  filter: 'blur(20px)',
                }}
              />
              <img
                src={player.profile_pic || '/placeholder.svg'}
                alt={player.full_name}
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  border: '5px solid #e0e0e0',
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: '#f5f5f5',
                }}
              />
              {/* Icon Team Name Overlay on Profile Pic */}
              {hasValue(player.is_icon) && player.is_icon && hasValue(player.icon_text) && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#ffc107',
                    color: '#000',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    zIndex: 2,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  {player.icon_text}
                </Box>
              )}
            </Box>
          </Box>

          {/* Player Name */}
          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#222',
              mb: 1,
              fontSize: { xs: '24px', sm: '28px' },
            }}
          >
            {player.full_name || 'Unknown Player'}
          </Typography>

          {/* Role Badge */}
          {hasValue(player.role) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Chip
                label={player.role}
                sx={{
                  backgroundColor: '#4dd0e1',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '4px 16px',
                  height: '32px',
                  borderRadius: '20px',
                }}
              />
            </Box>
          )}

          {/* Batting and Bowling Styles */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            {/* Batting Style */}
            {hasValue(player.cric_batting_style) && (
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  BATTING
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: '#222',
                    fontSize: '14px',
                  }}
                >
                  {player.cric_batting_style}
                </Typography>
              </Box>
            )}

            {/* Bowling Style */}
            {hasValue(player.cric_bowling_style) && (
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  BOWLING
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: '#222',
                    fontSize: '14px',
                  }}
                >
                  {player.cric_bowling_style}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Additional Details */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* District Name */}
            {hasValue(player.district_name) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px',
                  backgroundColor: '#fafafa',
                  borderRadius: '12px',
                }}
              >
                <LocationOnIcon
                  sx={{
                    color: '#42a5f5',
                    fontSize: '24px',
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '12px',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    District
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#222',
                      fontSize: '15px',
                    }}
                  >
                    {player.district_name}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Previous Club */}
            {hasValue(player.previous_team_details) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px',
                  backgroundColor: '#fafafa',
                  borderRadius: '12px',
                }}
              >
                <GroupIcon
                  sx={{
                    color: '#42a5f5',
                    fontSize: '24px',
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '12px',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Previous Club
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#222',
                      fontSize: '15px',
                    }}
                  >
                    {player.previous_team_details}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Icon Team Name (if different from overlay) */}
            {isIconPlayer(player.is_icon) && hasValue(player.icon_text) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px',
                  backgroundColor: '#fff9c4',
                  borderRadius: '12px',
                  border: '1px solid #ffc107',
                }}
              >
                <EmojiEventsIcon
                  sx={{
                    color: '#f57c00',
                    fontSize: '24px',
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '12px',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Icon Team
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#222',
                      fontSize: '15px',
                    }}
                  >
                    {player.icon_text}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const Index = () => {
  const [auctionData, setAuctionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveBlogEvents, setLiveBlogEvents] = useState([]);
  const liveBlogScrollRef = useRef(null);

  const aurlParams = new URLSearchParams(window.location.search);
  const ainviteCode = aurlParams.get('i');

  const auctionId = ainviteCode;
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [soldPlayer, setSoldPlayer] = useState(null);
  const socketRef = React.useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const pendingTabRef = useRef(null);
  const [youtubePlayerCollapsed, setYoutubePlayerCollapsed] = useState(false);
  const [youtubePlayerPosition, setYoutubePlayerPosition] = useState({ x: 0, y: 0 });
  const [youtubePlayerSize, setYoutubePlayerSize] = useState({ width: null, height: null }); // null means use default
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const isResizingRef = useRef(false);
  const youtubePlayerRef = useRef(null);
  
  // Search modal states
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ type: null, data: [] });
  const [suggestions, setSuggestions] = useState([]);
  
  // Player profile modal states
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // On mount, check localStorage for preferred tab
  useEffect(() => {
    const savedTab = localStorage.getItem('liveAuctionMainTab');
    if (savedTab === '0' || savedTab === '1') {
      setTabValue(Number(savedTab));
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Only persist for Teams (0) and Pending Players (1)
    if (newValue === 0 || newValue === 1) {
      localStorage.setItem('liveAuctionMainTab', String(newValue));
    }
    // Scroll to Pending Players tab if selected
    if (newValue === 1 && pendingTabRef.current) {
      pendingTabRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Auto-scroll to bottom when new live blog events are added
  useEffect(() => {
    if (liveBlogScrollRef.current && liveBlogEvents.length > 0) {
      liveBlogScrollRef.current.scrollTop = liveBlogScrollRef.current.scrollHeight;
    }
  }, [liveBlogEvents]);

  

  useEffect(() => {
    const effectId = Math.random().toString(36).substr(2, 5);
    console.log(`[DEBUG] useEffect mount, id=${effectId}`);
    fetchAuctionData();
    
    console.log('[DEBUG] Creating WebSocket connection to wss://auctionsnew.4cr.in');
    const socket = new WebSocket('wss://auctionsnew.4cr.in');
    
    socketRef.current = socket;
    console.log('[DEBUG] WebSocket instance created:', socket);
    console.log('[DEBUG] WebSocket readyState:', socket.readyState);
    
    // WebSocket event handlers
    socket.onopen = () => {
      console.log('[DEBUG] WebSocket connected successfully');
      console.log('[DEBUG] WebSocket readyState after connect:', socket.readyState);
      
      // Join auction room
      socket.send(JSON.stringify({
        type: 'joinAuction',
        auctionId: auctionId
      }));
      console.log('[DEBUG] Joined auction room:', auctionId);
    };
    
    socket.onclose = (event) => {
      console.log('[DEBUG] WebSocket disconnected:', event.code, event.reason);
    };
    
    socket.onerror = (error) => {
      console.error('[DEBUG] WebSocket error:', error);
      console.error('[DEBUG] Error details:', error);
      
      // Show user-friendly error message
      toast.error('Connection failed. Please refresh the page.');
    };
    
    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('[DEBUG] Received message:', message);
        
        // Handle different message types
        if (message.type === 'auctionEvent') {
          handleAuctionEvent(message);
        } else if (message.type === 'joinAuction') {
          console.log('[DEBUG] Successfully joined auction room');
        } else {
          // Handle other message types if needed
          console.log('[DEBUG] Unhandled message type:', message.type);
        }
      } catch (error) {
        console.error('[DEBUG] Error parsing WebSocket message:', error);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      console.log(`[DEBUG] useEffect cleanup, id=${effectId}`);
      console.log('[DEBUG] Cleaning up: closing WebSocket connection');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    };
  }, []);

  const fetchAuctionData = async () => {
    try {
      // Get invite code from query string
      const urlParams = new URLSearchParams(window.location.search);
      const inviteCode = urlParams.get('i');
      
      if (!inviteCode) {
        throw new Error('Invite code is required');
      }

      const response = await fetch(global.config.app.backend_api_url + 'auction/get_current_auction_status_by_invite_code/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invite_code: inviteCode,
          cookie_id: getOrCreateCookieId()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAuctionData(data);
      
      // Set live blog events if available
      if (data.live_blog_events && Array.isArray(data.live_blog_events)) {
        setLiveBlogEvents(data.live_blog_events);
      }
    } catch (error) {
      console.error('Error fetching auction data:', error);
      toast.error('Failed to load auction data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Search Tracking Function
  const trackSearchInteraction = async (searchKeyword = null, iconClicked = null) => {
    try {
      if (!auctionData || !auctionData.auction_details || !auctionData.auction_details.main || !auctionData.auction_details.main[0]) {
        return;
      }

      const seriesId = auctionData.auction_details.main[0].id || auctionData.auction_details.main[0].series_id;
      
      if (!seriesId) {
        console.warn('[Search Tracking] series_id not available');
        return;
      }

      // At least one of search_keyword or icon_clicked must be provided
      if (!searchKeyword && !iconClicked) {
        return;
      }

      const payload = {
        series_id: seriesId,
        cookie_id: getOrCreateCookieId()
      };

      if (searchKeyword) {
        payload.search_keyword = searchKeyword.trim().substring(0, 255);
      }

      if (iconClicked) {
        payload.icon_clicked = iconClicked.substring(0, 100);
      }

      // Make non-blocking API call
      fetch(global.config.app.backend_api_url + 'auction/track_public_search_interaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      })
      .then(response => {
        // Check if response is ok before parsing JSON
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // Optional: Log success (usually silent)
        if (data.status === 1) {
          console.log('[Search Tracking] Tracked successfully');
        } else {
          console.warn('[Search Tracking] API returned status 0:', data.message);
        }
      })
      .catch(error => {
        // Fail silently - don't interrupt user experience
        console.error('[Search Tracking] Error:', error);
      });
    } catch (error) {
      // Fail silently
      console.error('[Search Tracking] Error:', error);
    }
  };

  // Search Functions
  const handleOpenSearchModal = () => {
    setSearchModalOpen(true);
    setSearchTerm('');
    setSearchResults({ type: null, data: [] });
    setSuggestions([]);
    // Track search icon click
    trackSearchInteraction(null, 'search_button');
    // Refresh auction data
    fetchAuctionData();
  };

  const handleCloseSearchModal = () => {
    setSearchModalOpen(false);
    setSearchTerm('');
    setSearchResults({ type: null, data: [] });
    setSuggestions([]);
  };

  const performSearch = (term) => {
    if (!auctionData || !term.trim()) {
      setSearchResults({ type: null, data: [] });
      setSuggestions([]);
      return;
    }

    const searchLower = term.toLowerCase().trim();
    const teams = auctionData.auction_details.teams || [];
    const pending = auctionData.auction_details.pending || [];

    // Check for keyword searches first
    if (searchLower === 'sold') {
      const soldPlayers = [];
      teams.forEach(team => {
        if (team.players && team.players.length > 0) {
          team.players.forEach(player => {
            soldPlayers.push({
              ...player,
              team_name: team.team_name,
              team_logo: team.team_logo,
              status: 'sold'
            });
          });
        }
      });
      setSearchResults({ type: 'keyword', keyword: 'sold', data: soldPlayers });
      setSuggestions([]);
      return;
    }

    if (searchLower === 'unsold') {
      const unsoldPlayers = pending.filter(p => 
        p.auction_sold_status === '2' || p.auction_sold_status === 2
      ).map(p => ({ ...p, status: 'unsold' }));
      setSearchResults({ type: 'keyword', keyword: 'unsold', data: unsoldPlayers });
      setSuggestions([]);
      return;
    }

    if (searchLower === 'pending') {
      const pendingPlayers = pending.filter(p => 
        p.auction_sold_status !== '2' && p.auction_sold_status !== 2
      ).map(p => ({ ...p, status: 'pending' }));
      setSearchResults({ type: 'keyword', keyword: 'pending', data: pendingPlayers });
      setSuggestions([]);
      return;
    }

    // Check if it's a number (SL No search)
    if (!isNaN(searchLower) && searchLower !== '') {
      const slNo = searchLower;
      let foundPlayer = null;
      let playerStatus = null;

      // Search in sold players
      teams.forEach(team => {
        if (team.players && team.players.length > 0) {
          const player = team.players.find(p => p.sl_no === slNo);
          if (player) {
            foundPlayer = {
              ...player,
              team_name: team.team_name,
              team_logo: team.team_logo,
              status: 'sold'
            };
          }
        }
      });

      // Search in pending players
      if (!foundPlayer) {
        const player = pending.find(p => p.sl_no === slNo);
        if (player) {
          foundPlayer = {
            ...player,
            status: (player.auction_sold_status === '2' || player.auction_sold_status === 2) ? 'unsold' : 'pending'
          };
        }
      }

      if (foundPlayer) {
        setSearchResults({ type: 'player', data: [foundPlayer] });
        setSuggestions([]);
      } else {
        setSearchResults({ type: 'notfound', data: [] });
        setSuggestions([]);
      }
      return;
    }

    // Search for team names
    const teamMatches = teams.filter(team => 
      team.team_name.toLowerCase().includes(searchLower)
    );

    // Search for player names
    const playerMatches = [];
    
    // Search in sold players
    teams.forEach(team => {
      if (team.players && team.players.length > 0) {
        team.players.forEach(player => {
          if (player.full_name.toLowerCase().includes(searchLower)) {
            playerMatches.push({
              ...player,
              team_name: team.team_name,
              team_logo: team.team_logo,
              status: 'sold'
            });
          }
        });
      }
    });

    // Search in pending players
    pending.forEach(player => {
      if (player.full_name.toLowerCase().includes(searchLower)) {
        playerMatches.push({
          ...player,
          status: (player.auction_sold_status === '2' || player.auction_sold_status === 2) ? 'unsold' : 'pending'
        });
      }
    });

    // Generate suggestions
    const newSuggestions = [];
    
    if (teamMatches.length > 0) {
      teamMatches.forEach(team => {
        newSuggestions.push({
          type: 'team',
          label: team.team_name,
          data: team
        });
      });
    }

    if (playerMatches.length > 0) {
      playerMatches.slice(0, 10).forEach(player => {
        newSuggestions.push({
          type: 'player',
          label: player.full_name,
          data: player
        });
      });
    }

    setSuggestions(newSuggestions);

    // If exact match or single result, show it
    if (teamMatches.length === 1 && playerMatches.length === 0) {
      handleTeamSelect(teamMatches[0]);
    } else if (playerMatches.length === 1 && teamMatches.length === 0) {
      setSearchResults({ type: 'player', data: [playerMatches[0]] });
    } else if (newSuggestions.length === 0) {
      setSearchResults({ type: 'notfound', data: [] });
    }
  };

  const handleTeamSelect = (team) => {
    setSearchResults({ type: 'team', data: [team] });
    setSuggestions([]);
  };

  const handlePlayerSelect = (player) => {
    setSearchResults({ type: 'player', data: [player] });
    setSuggestions([]);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'team') {
      handleTeamSelect(suggestion.data);
    } else if (suggestion.type === 'player') {
      handlePlayerSelect(suggestion.data);
    }
    setSearchTerm(suggestion.label);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    performSearch(value);
  };

  const handleSearchKeyPress = (e) => {
    // Track search when user presses Enter
    if (e.key === 'Enter' && searchTerm.trim().length > 0) {
      trackSearchInteraction(searchTerm.trim(), null);
    }
  };

  // Player profile modal handlers
  const handleOpenPlayerProfile = (player) => {
    setSelectedPlayer(player);
    setProfileModalOpen(true);
  };

  const handleClosePlayerProfile = () => {
    setProfileModalOpen(false);
    setSelectedPlayer(null);
  };

  const UndosoldDisplay = ({ player }) => {
  
    return (
      <div className="container mt-12">
          <div className="col-3">
            <img
              src={player.img}
              alt={player.name}
              className="img-fluid rounded"
              style={{ maxHeight: '100px' }}
            />
          </div>
          <div className="col-9">
            <h5 className="mb-1">{player.name}&nbsp;&nbsp;[{player.pdf_sl_no}]</h5>
            <p
              className={`mb-0 text-success`}
            >
              Reversed
            </p>
          </div>
        </div>
    );
  };
  const SoldDisplay = ({ player }) => {
  
    return (
      <div className="container mt-12">
          <div className="col-3">
            <img
              src={player.img}
              alt={player.name}
              className="img-fluid rounded"
              style={{ maxHeight: '100px' }}
            />
          </div>
          <div className="col-9">
            <h5 className="mb-1">{player.name}&nbsp;&nbsp;[{player.pdf_sl_no}]</h5>
            <p
              className={`mb-0 text-success`}
            >
              Sold to {player.team} for {player.amount}
            </p>
          </div>
        </div>
    );
  };

  const handleAuctionEvent = (message) => {
    console.log('[DEBUG] handleAuctionEvent called', message);
    const { eventType, data } = message;
    console.log(eventType);
    console.log(data);
    
    switch (eventType) {
      case 'biddingStart':
        setCurrentPlayer(data);
        break;
      case 'playerSold':
        setShowConfetti(true);
        setSoldPlayer(data);
        setTimeout(() => setShowConfetti(false), 5000);
        setCurrentPlayer(null);
        fetchAuctionData();
        break;
      case 'NewPlayer':
        toast.info('New Player Added');
        break;
      case 'Unsold':
        toast.warning(<UnsoldDisplay player={data} />);
        fetchAuctionData();
        break;
      case 'ChangeTeam':
        toast.info('Team Changed');
        fetchAuctionData();
        break;
      case 'UndoSold':
        toast.info('Undo Sold');
        fetchAuctionData();
        break;
      case 'live_blogs_events':
        // Handle live blog events from socket
        if (data && Array.isArray(data)) {
          setLiveBlogEvents(prevEvents => [...prevEvents, ...data]);
        } else if (data && typeof data === 'object') {
          // Handle single event object
          setLiveBlogEvents(prevEvents => [...prevEvents, data]);
        }
        break;
      default:
        break;
    }
  };


const UnsoldDisplay = ({ player }) => {
  
  return (
    <div className="container mt-12">
        <div className="col-3">
          <img
            src={player.img}
            alt={player.name}
            className="img-fluid rounded"
            style={{ maxHeight: '100px' }}
          />
        </div>
        <div className="col-9">
          <h5 className="mb-1">{player.name}&nbsp;&nbsp;[{player.pdf_sl_no}]</h5>
          <p
            className={`mb-0 text-success`}
          >
            Player unsold
          </p>
        </div>
      </div>
  );
};
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && socketRef.current && socketRef.current.readyState === WebSocket.CLOSED) {
      // Reconnect WebSocket if it's closed when page becomes visible
      const newSocket = new WebSocket('wss://auctionsnew.4cr.in');
      socketRef.current = newSocket;
      
      newSocket.onopen = () => {
        console.log('[DEBUG] WebSocket reconnected on visibility change');
        newSocket.send(JSON.stringify({
          type: 'joinAuction',
          auctionId: auctionId
        }));
      };
      
      newSocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'auctionEvent') {
            handleAuctionEvent(message);
          }
        } catch (error) {
          console.error('[DEBUG] Error parsing WebSocket message:', error);
        }
      };
    }
  };

  // YouTube Player Drag Handlers
  const handleDragStart = (e) => {
    if (youtubePlayerCollapsed) return; // Don't drag when collapsed
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    if (youtubePlayerRef.current) {
      const rect = youtubePlayerRef.current.getBoundingClientRect();
      dragOffsetRef.current = {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    }
    isDraggingRef.current = true;
    setIsDragging(true);
    e.preventDefault();
  };

  // Add global event listeners for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleDrag = (e) => {
      if (!isDraggingRef.current || youtubePlayerCollapsed) return;
      
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      // Use current player size (dynamic or default)
      const defaultWidth = window.innerWidth < 768 ? 200 : 320;
      const defaultHeight = window.innerWidth < 768 ? 112.5 : 180;
      const playerWidth = youtubePlayerSize.width || defaultWidth;
      const playerHeight = youtubePlayerSize.height || defaultHeight;
      
      // Calculate new position, keeping player within viewport
      let newX = clientX - dragOffsetRef.current.x;
      let newY = clientY - dragOffsetRef.current.y;
      
      // Constrain to viewport bounds
      newX = Math.max(0, Math.min(newX, windowWidth - playerWidth));
      newY = Math.max(0, Math.min(newY, windowHeight - playerHeight));
      
      setYoutubePlayerPosition({ x: newX, y: newY });
      e.preventDefault();
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      setIsDragging(false);
    };

    const handleMouseMove = (e) => handleDrag(e);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e) => handleDrag(e);
    const handleTouchEnd = () => handleDragEnd();
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, youtubePlayerCollapsed, youtubePlayerSize]);

  // YouTube Player Resize Handlers
  const handleResizeStart = (e) => {
    if (youtubePlayerCollapsed) return; // Don't resize when collapsed
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    if (youtubePlayerRef.current) {
      const rect = youtubePlayerRef.current.getBoundingClientRect();
      const currentWidth = youtubePlayerSize.width || (window.innerWidth < 768 ? 200 : 320);
      const currentHeight = youtubePlayerSize.height || (window.innerWidth < 768 ? 112.5 : 180);
      
      resizeStartRef.current = {
        x: clientX,
        y: clientY,
        width: currentWidth,
        height: currentHeight
      };
    }
    isResizingRef.current = true;
    setIsResizing(true);
    e.preventDefault();
    e.stopPropagation();
  };

  // Add global event listeners for resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleResize = (e) => {
      if (!isResizingRef.current || youtubePlayerCollapsed) return;
      
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      
      const deltaX = clientX - resizeStartRef.current.x;
      const deltaY = clientY - resizeStartRef.current.y;
      
      // Calculate new size (maintain 16:9 aspect ratio)
      const aspectRatio = 16 / 9;
      const minWidth = window.innerWidth < 768 ? 150 : 200;
      const maxWidth = window.innerWidth < 768 ? 400 : 800;
      const minHeight = minWidth / aspectRatio;
      const maxHeight = maxWidth / aspectRatio;
      
      // Resize from bottom-right corner
      let newWidth = resizeStartRef.current.width + deltaX;
      let newHeight = resizeStartRef.current.height + deltaY;
      
      // Maintain aspect ratio based on width change
      newWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
      newHeight = newWidth / aspectRatio;
      
      // Ensure height is within bounds
      if (newHeight < minHeight) {
        newHeight = minHeight;
        newWidth = newHeight * aspectRatio;
      } else if (newHeight > maxHeight) {
        newHeight = maxHeight;
        newWidth = newHeight * aspectRatio;
      }
      
      setYoutubePlayerSize({ width: newWidth, height: newHeight });
      e.preventDefault();
    };

    const handleResizeEnd = () => {
      isResizingRef.current = false;
      setIsResizing(false);
    };

    const handleMouseMove = (e) => handleResize(e);
    const handleMouseUp = () => handleResizeEnd();
    const handleTouchMove = (e) => handleResize(e);
    const handleTouchEnd = () => handleResizeEnd();
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isResizing, youtubePlayerCollapsed, youtubePlayerSize]);

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!auctionData) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger" role="alert">
          Failed to load auction data.
        </div>
      </div>
    );
  }

  const auctionMain = auctionData.auction_details.main[0];
  const teams = auctionData.auction_details.teams;

  // Calculate pending players count
  const pendingPlayersCount = auctionData && auctionData.auction_details && auctionData.auction_details.pending ? auctionData.auction_details.pending.length : 0;

  // Get YouTube video ID if available
  const youtubeUrl = auctionMain?.youtube_live_url;
  const youtubeVideoId = youtubeUrl ? getYouTubeVideoId(youtubeUrl) : null;
  const showYouTubePlayer = youtubeVideoId && youtubeUrl.trim() !== '';

  // Calculate default and current player size
  const defaultWidth = window.innerWidth < 768 ? 200 : 320;
  const defaultHeight = window.innerWidth < 768 ? 112.5 : 180;
  const playerWidth = youtubePlayerSize.width || defaultWidth;
  const playerHeight = youtubePlayerSize.height || defaultHeight;

  return (
    <div className="container-fluid py-3 bg-light min-vh-100">
      {/* YouTube Live Stream Player - Resizable Picture-in-Picture */}
      {showYouTubePlayer && (
        <>
          <style>{`
            .youtube-pip-container {
              position: fixed;
              z-index: 1000;
              background: #000;
              border-radius: 8px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.5);
              overflow: visible;
              transition: ${isDragging || isResizing ? 'none' : 'transform 0.3s ease-in-out'};
            }
            .youtube-pip-container.dragging {
              cursor: move;
              user-select: none;
            }
            .youtube-pip-container.resizing {
              user-select: none;
            }
            .youtube-drag-handle {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 30px;
              cursor: move;
              z-index: 1002;
              background: linear-gradient(to bottom, rgba(0,0,0,0.3), transparent);
            }
            .youtube-resize-handle {
              position: absolute;
              bottom: 0;
              right: 0;
              width: 20px;
              height: 20px;
              cursor: nwse-resize;
              z-index: 1003;
              background: rgba(255, 255, 255, 0.3);
              border-top-left-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .youtube-resize-handle:hover {
              background: rgba(255, 255, 255, 0.5);
            }
            .youtube-resize-handle::after {
              content: '';
              width: 0;
              height: 0;
              border-style: solid;
              border-width: 0 0 12px 12px;
              border-color: transparent transparent rgba(255, 255, 255, 0.8) transparent;
            }
            @media (max-width: 768px) {
              .youtube-pip-container:not(.has-position) {
                top: 10px;
                right: 10px;
              }
              .youtube-pip-container.collapsed {
                transform: translateX(calc(100% - 50px));
              }
              .youtube-toggle-btn {
                padding: 4px 8px !important;
                font-size: 10px !important;
              }
              .youtube-toggle-btn span:last-child {
                display: none !important;
              }
              .youtube-resize-handle {
                width: 16px;
                height: 16px;
              }
            }
            @media (min-width: 769px) {
              .youtube-pip-container:not(.has-position) {
                top: 20px;
                right: 20px;
              }
              .youtube-pip-container.collapsed {
                transform: translateX(calc(100% - 60px));
              }
            }
            .youtube-pip-iframe {
              width: 100%;
              height: 100%;
              border: none;
              pointer-events: ${isDragging || isResizing ? 'none' : 'auto'};
            }
          `}</style>
          <div
            ref={youtubePlayerRef}
            className={`youtube-pip-container ${youtubePlayerCollapsed ? 'collapsed' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${youtubePlayerPosition.x !== 0 || youtubePlayerPosition.y !== 0 ? 'has-position' : ''}`}
            style={{
              width: `${playerWidth}px`,
              height: `${playerHeight}px`,
              left: youtubePlayerPosition.x !== 0 || youtubePlayerPosition.y !== 0 ? `${youtubePlayerPosition.x}px` : 'auto',
              top: youtubePlayerPosition.x !== 0 || youtubePlayerPosition.y !== 0 ? `${youtubePlayerPosition.y}px` : 'auto',
              right: youtubePlayerPosition.x !== 0 || youtubePlayerPosition.y !== 0 ? 'auto' : undefined
            }}
          >
            {/* Drag Handle */}
            {!youtubePlayerCollapsed && (
              <div
                className="youtube-drag-handle"
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
              />
            )}

            {/* Resize Handle */}
            {!youtubePlayerCollapsed && (
              <div
                className="youtube-resize-handle"
                onMouseDown={handleResizeStart}
                onTouchStart={handleResizeStart}
                title="Resize player"
              />
            )}

            {/* Collapse/Expand Button */}
            <button
              className="youtube-toggle-btn"
              onClick={() => setYoutubePlayerCollapsed(!youtubePlayerCollapsed)}
              style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                zIndex: 1001,
                backgroundColor: 'rgba(255, 0, 0, 0.85)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.3s ease',
                lineHeight: 1
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 0, 0, 1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 0, 0, 0.85)';
              }}
            >
              <span>🎥</span>
              <span>{youtubePlayerCollapsed ? '▶' : '✕'}</span>
            </button>

            {/* YouTube Embed - Dynamic Size */}
            <iframe
              className="youtube-pip-iframe"
              src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=0&controls=1&rel=0&modestbranding=1&playsinline=1`}
              title="YouTube Live Stream"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </>
      )}

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-primary text-white">
            <div className="card-body text-center" style={{ position: 'relative' }}>
              {/* Search Icon Button */}
              <IconButton
                onClick={handleOpenSearchModal}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                <SearchIcon />
              </IconButton>
              
              <h1 className="card-title h3 mb-2">{auctionMain.series_name}</h1>
              <div className="row">
                <div className="col-12">
                  <small className="text-light">Total Teams</small>
                  <div className="h5 mb-0">{auctionMain.no_of_teams}</div>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auction Stats */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-warning">
            <div className="card-header bg-warning text-dark">
              <h5 className="card-title mb-0">📊 Auction Statistics</h5>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-3">
                  <div className="h4 text-primary">{auctionMain.count_players_in_auction}</div>
                  <small className="text-muted">Total Players</small>
                </div>
                <div className="col-3">
                  <div className="h4 text-success">{auctionMain.sold_count}</div>
                  <small className="text-muted">Sold</small>
                </div>
                <div className="col-3">
                  <div className="h4 text-danger">{auctionMain.unsold_count}</div>
                  <small className="text-muted">Unsold</small>
                </div>
                <div className="col-3">
                  <div
                    className="h4 text-warning"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    title="View Pending Players"
                    onClick={() => {
                      setTabValue(1);
                      if (pendingTabRef.current) {
                        pendingTabRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') {
                      setTabValue(1);
                      if (pendingTabRef.current) {
                        pendingTabRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}}
                  >
                    {auctionMain.pending_count}
                  </div>
                  <small className="text-muted">Pending</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Summary */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-info">
            <div className="card-header bg-info text-white">
              <h5 className="card-title mb-0">🏏 Team Summary</h5>
            </div>
            <div className="card-body p-0" style={{ position: 'relative' }}>
              <div 
                className="table-responsive"
                style={{ 
                  filter: auctionMain.hide_team_summary_info === "1" ? 'blur(3px)' : 'none',
                  transition: 'filter 0.3s ease'
                }}
              >
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Team Name</th>
                      <th>Max Bid</th>
                      <th>Players</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team, index) => {
                      const currentPlayers = parseInt(team.current_players_count);
                      const maxPlayers = parseInt(team.team_max_playes_count);
                      const progressPercentage = (currentPlayers / maxPlayers) * 100;
                      
                      const isTeamFull = progressPercentage === 100;
                      // Check if current player's bid is greater than or equal to team's max bid
                      const isBidExceeded = currentPlayer && 
                        parseFloat(currentPlayer.amount) >= parseFloat(team.max_bid_for_a_player);
                      
                      // Determine row highlight
                      let rowClass = '';
                      let rowStyle = {};
                      if (isTeamFull) {
                        rowClass = 'table-success';
                        rowStyle = { backgroundColor: '#d4edda', borderColor: '#c3e6cb' }; // greenish for full
                      } else if (isBidExceeded) {
                        rowClass = 'table-danger';
                        rowStyle = { backgroundColor: '#f8d7da', borderColor: '#f5c6cb' }; // reddish for bid limit
                      }
                      
                      return (
                        <tr 
                          key={index}
                          className={rowClass}
                          style={rowStyle}
                        >
                          <td>
                            <span className="d-inline-flex align-items-center">
                              <img
                                src={team.team_logo && team.team_logo.trim() !== '' ? team.team_logo : '/placeholder.svg'}
                                alt={team.team_name + ' logo'}
                                className="rounded-circle me-2"
                                width="32"
                                height="32"
                                style={{ objectFit: 'cover', background: '#fff' }}
                                onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                              />
                              <a 
                                href={`#team-${team.team_name.toLowerCase()}`}
                                className="fw-bold text-dark text-decoration-none"
                                style={{ cursor: 'pointer', fontWeight: 600, border: 'none' }}
                              >
                                {team.team_name}
                              </a>
                            </span>
                          </td>
                          <td className="text-success fw-bold" style={{ textDecoration: isTeamFull ? 'line-through' : 'none' }}>
                            {team.max_bid_for_a_player}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1 me-3">
                                <div className="progress" style={{ height: '20px' }}>
                                  <div 
                                    className={`progress-bar ${progressPercentage >= 100 ? 'bg-success' : progressPercentage >= 80 ? 'bg-warning' : 'bg-primary'}`}
                                    role="progressbar" 
                                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                                    aria-valuenow={currentPlayers} 
                                    aria-valuemin={0} 
                                    aria-valuemax={maxPlayers}
                                  >
                                    {currentPlayers}/{maxPlayers}
                                  </div>
                                </div>
                              </div>
                              
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Upgrade Message Overlay */}
              {auctionMain.hide_team_summary_info === "1" && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  zIndex: 10
                }}>
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔒</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                      Feature Not Available
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>
                      Upgrade your package to view Team Summary
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      

      {/* Tabs for different sections */}
      <div className="row">
        <div className="col-12">
          <Card>
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom>
                Live Auction Dashboard
              </Typography>
              
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="scrollable"
                scrollButtons="auto"
                sx={{ 
                  mb: 2,
                  '& .MuiTabs-flexContainer': {
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  },
                  '& .MuiTab-root': {
                    minHeight: 48,
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '12px',
                    margin: '4px',
                    minWidth: 'auto',
                    flex: '0 0 auto',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    },
                    '&.Mui-selected': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    }
                  }
                }}
              >
                <Tab 
                  label="Teams" 
                  sx={{
                    bgcolor: tabValue === 0 ? '#1976d2' : '#e3f2fd',
                    color: tabValue === 0 ? '#fff' : '#1976d2',
                    fontWeight: 700,
                    borderRadius: 3,
                    minWidth: { xs: 80, sm: 100 },
                    fontSize: { xs: '12px', sm: '14px' },
                    py: 1.5,
                    px: 2
                  }}
                />
                <Tab 
                  label={
                    <span style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 4,
                      flexDirection: window.innerWidth < 600 ? 'column' : 'row',
                      fontSize: window.innerWidth < 600 ? '10px' : '14px'
                    }}>
                      <span role="img" aria-label="pending">⏳</span>
                      <span>Pending</span>
                      <span className="badge bg-danger" style={{ 
                        marginLeft: window.innerWidth < 600 ? 0 : 4, 
                        fontSize: window.innerWidth < 600 ? 10 : 12,
                        marginTop: window.innerWidth < 600 ? 2 : 0
                      }}>
                        {pendingPlayersCount}
                      </span>
                    </span>
                  }
                  sx={{
                    bgcolor: tabValue === 1 ? '#6f42cf' : '#ede7ff',
                    color: tabValue === 1 ? '#fff' : '#6f42ff',
                    fontWeight: 700,
                    borderRadius: 3,
                    minWidth: { xs: 80, sm: 100 },
                    fontSize: { xs: '12px', sm: '14px' },
                    py: 1.5,
                    px: 2
                  }}
                />
                
                <Tab 
                  label="Recent" 
                  sx={{
                    bgcolor: tabValue === 2 ? '#388e3c' : '#e8f5e9',
                    color: tabValue === 2 ? '#fff' : '#388e3c',
                    fontWeight: 700,
                    borderRadius: 3,
                    minWidth: { xs: 80, sm: 100 },
                    fontSize: { xs: '12px', sm: '14px' },
                    py: 1.5,
                    px: 2
                  }}
                />
                <Tab 
                  label="Top" 
                  sx={{
                    bgcolor: tabValue === 3 ? '#ff9800' : '#fff3e0',
                    color: tabValue === 3 ? '#fff' : '#ff9800',
                    fontWeight: 700,
                    borderRadius: 3,
                    minWidth: { xs: 80, sm: 100 },
                    fontSize: { xs: '12px', sm: '14px' },
                    py: 1.5,
                    px: 2
                  }}
                />


              

                <Tab 
                  label={
                    <span style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 4,
                      flexDirection: window.innerWidth < 600 ? 'column' : 'row',
                      fontSize: window.innerWidth < 600 ? '10px' : '14px'
                    }}>
                      <span role="img" aria-label="live">📱</span>
                      <span>Live Blog</span>
                      <span className="badge bg-success" style={{ 
                        marginLeft: window.innerWidth < 600 ? 0 : 4, 
                        fontSize: window.innerWidth < 600 ? 10 : 12,
                        marginTop: window.innerWidth < 600 ? 2 : 0
                      }}>
                        {liveBlogEvents.length}
                      </span>
                    </span>
                  }
                  sx={{
                    bgcolor: tabValue === 4 ? '#28a745' : '#d4edda',
                    color: tabValue === 4 ? '#fff' : '#28a745',
                    fontWeight: 700,
                    borderRadius: 3,
                    minWidth: { xs: 80, sm: 100 },
                    fontSize: { xs: '12px', sm: '14px' },
                    py: 1.5,
                    px: 2
                  }}
                />
              </Tabs>

              {/* Teams Tab */}
              {tabValue === 0 && (
                <Box sx={{ mt: 2 }}>
                  <div className="row">
                    {teams.map((team, index) => {
                      const currentPlayers = parseInt(team.current_players_count);
                      const maxPlayers = parseInt(team.team_max_playes_count);
                      const isTeamFull = currentPlayers === maxPlayers;
                      
                      return (
                      <div key={index} className="col-12 col-md-6 mb-3">
                        <div className="card h-100" id={`team-${team.team_name.toLowerCase()}`}>
                          <div className="card-header bg-info text-white">
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0 d-inline-flex align-items-center">
                                <img
                                  src={team.team_logo && team.team_logo.trim() !== '' ? team.team_logo : '/placeholder.svg'}
                                  alt={team.team_name + ' logo'}
                                  className="rounded-circle me-2"
                                  width="32"
                                  height="32"
                                  style={{ objectFit: 'cover', background: '#fff' }}
                                  onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                                />
                                {team.team_name}
                              </h6>
                              <span className="badge bg-light text-dark">
                                {team.current_players_count}/{team.team_max_playes_count} players
                              </span>
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="row mb-3">
                              <div className="col-6">
                                <small className="text-muted">Remaining Budget</small>
                                <div className="h5 text-success">{team.current_wallet_balance}</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">Max Bid for Player</small>
                                <div className="h5 text-warning" style={{ textDecoration: isTeamFull ? 'line-through' : 'none' }}>{team.max_bid_for_a_player}</div>
                              </div>
                            </div>
                            <div className="row mb-3">
                              <div className="col-12">
                                <a 
                                  href={`/teamplayers?t=${team.team_hash_code}`}
                                  className="btn btn-primary btn-sm w-100"
                                >
                                  View Team Poster
                                </a>
                              </div>
                            </div>
                            <div className="row">
                              {team.players.length > 0 ? (
                                team.players.map((player, pIndex) => (
                                  <div key={pIndex} className="col-12 mb-2">
                                    <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded" style={{ height: '64px' }}>
                                      <div className="d-flex align-items-center h-100">
                                        <img 
                                          src={player.profile_pic} 
                                          alt={player.full_name}
                                          className="rounded-circle me-2"
                                          width="40"
                                          height="40"
                                          style={{ objectFit: 'cover' }}
                                          onError={(e) => {
                                            e.currentTarget.src = '/placeholder.svg';
                                          }}
                                        />
                                        <div>
                                          <div 
                                            className="fw-bold"
                                            style={{ cursor: 'pointer', color: '#0d6efd' }}
                                            onClick={() => handleOpenPlayerProfile(player)}
                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                          >
                                            {player.full_name}
                                          </div>
                                          <small className="text-muted">
                                            SL: {player.sl_no}
                                          </small>
                                        </div>
                                      </div>
                                      <div className="text-end">
                                        <div className="text-success fw-bold">
                                          {player.auction_sold_amount}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="col-12">
                                  <div className="text-center text-muted py-3">
                                    No players yet
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </Box>
              )}

              {/* Live Blog Tab */}
              {tabValue === 4 && (
                <Box sx={{ mt: 2 }}>
                  <div className="card">
                    <div className="card-header" style={{ 
                      background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                      color: 'white',
                      position: 'relative'
                    }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">📱 Live Auction Feed</h5>
                        <div className="d-flex align-items-center">
                          <div className="badge bg-danger me-2" style={{ animation: 'blink 1s infinite' }}>
                            LIVE
                          </div>
                          <small>{liveBlogEvents.length} events</small>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="card-body p-0" 
                      ref={liveBlogScrollRef}
                      style={{ 
                        height: '600px', 
                        overflowY: 'auto',
                        background: '#f8f9fa'
                      }}
                    >
                      {liveBlogEvents.length > 0 ? (
                        liveBlogEvents.map((event, index) => (
                          <div key={`${event.event_id || index}-${event.event_timestamp || Date.now()}`} className="p-3 border-bottom bg-white" style={{ marginBottom: '8px' }}>
                            <div className="d-flex align-items-start">
                              <div className="position-relative me-3">
                                <img 
                                  src={event.player_profile_pic || '/placeholder.svg'} 
                                  alt={event.player_name || 'Player'}
                                  className="rounded-circle"
                                  width="48"
                                  height="48"
                                  style={{ objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.currentTarget.src = '/placeholder.svg';
                                  }}
                                />
                                <div 
                                  className="position-absolute bottom-0 end-0 rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: getEventTypeColor(event.event_type),
                                    border: '2px solid white'
                                  }}
                                >
                                  {getEventTypeIcon(event.event_type)}
                                </div>
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <div>
                                    <span className="fw-bold">{event.player_name || 'Unknown Player'}</span>
                                    <span className="badge bg-secondary ms-2" style={{ fontSize: '10px' }}>
                                      {getPlayerRole(event.player_name || '')}
                                    </span>
                                  </div>
                                  <small className="text-muted">
                                    {event.event_timestamp ? new Date(event.event_timestamp).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true
                                    }) : 'Just now'}
                                  </small>
                                </div>
                                <div 
                                  className="mb-1"
                                  style={{ 
                                    color: getEventTypeColor(event.event_type),
                                    fontWeight: 500
                                  }}
                                >
                                  {Parser(event.event_message || 'Event occurred')}
                                </div>
                                
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted py-5">
                          <div className="mb-3">
                            <span style={{ fontSize: '48px' }}>📱</span>
                          </div>
                          <h6>No live events yet</h6>
                          <small>Events will appear here as the auction progresses</small>
                        </div>
                      )}
                    </div>
                  </div>
                </Box>
              )}

              {/* Recent Sells Tab */}
              {tabValue === 2 && (
                <Box sx={{ mt: 2 }}>
                  <div className="card">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">🕒 Recent</h5>
                    </div>
                    <div className="card-body p-0">
                      {auctionData.auction_details.recent_sell.length > 0 ? (
                        auctionData.auction_details.recent_sell.map((player, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                            <div className="d-flex align-items-center">
                              <img 
                                src={player.profile_pic} 
                                alt={player.full_name}
                                className="rounded-circle me-3"
                                width="48"
                                height="48"
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder.svg';
                                }}
                              />
                              <div>
                                <div 
                                  className="fw-bold"
                                  style={{ cursor: 'pointer', color: '#0d6efd' }}
                                  onClick={() => handleOpenPlayerProfile(player)}
                                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                  {player.full_name}
                                </div>
                                <small className="text-muted">
                                  SL: {player.sl_no} | Team: {player.team_name}
                                </small>
                                <br />
                                <small className="text-muted">
                                  {new Date(player.auction_action_on).toLocaleString()}
                                </small>
                              </div>
                            </div>
                            <div className="text-end">
                              <div className="h5 text-success mb-0">
                                {player.auction_sold_amount}
                              </div>
                              <span className="badge bg-success">SOLD</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted py-4">
                          No recent sells
                        </div>
                      )}
                    </div>
                  </div>
                </Box>
              )}

              {/* Top Sells Tab */}
              {tabValue === 3 && (
                <Box sx={{ mt: 2 }}>
                  <div className="card">
                    <div className="card-header bg-warning text-dark">
                      <h5 className="mb-0">💰 Top </h5>
                    </div>
                    <div className="card-body p-0">
                      {auctionData.auction_details.top_sell.length > 0 ? (
                        auctionData.auction_details.top_sell.map((player, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                            <div className="d-flex align-items-center">
                              <div className="badge bg-primary me-3 fs-6">#{index + 1}</div>
                              <img 
                                src={player.profile_pic} 
                                alt={player.full_name}
                                className="rounded-circle me-3"
                                width="48"
                                height="48"
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder.svg';
                                }}
                              />
                              <div>
                                <div 
                                  className="fw-bold"
                                  style={{ cursor: 'pointer', color: '#0d6efd' }}
                                  onClick={() => handleOpenPlayerProfile(player)}
                                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                  {player.full_name}
                                </div>
                                <small className="text-muted">
                                  SL: {player.sl_no} | Team: {player.team_name}
                                </small>
                                <br />
                                <small className="text-muted">
                                  {new Date(player.auction_action_on).toLocaleString()}
                                </small>
                              </div>
                            </div>
                            <div className="text-end">
                              <div className="h5 text-warning mb-0">
                                {player.auction_sold_amount}
                              </div>
                              <span className="badge bg-success">SOLD</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted py-4">
                          No top sells yet
                        </div>
                      )}
                    </div>
                  </div>
                </Box>
              )}

              {/* Pending Players Tab */}
              {tabValue === 1 && (
                <Box sx={{ mt: 2 }} ref={pendingTabRef}>
                  {/* Pending and Unsold Counts */}
                  <div className="mb-3 d-flex gap-3 align-items-center">
                    <span className="badge bg-primary" style={{ fontSize: 16 }}>
                      Pending: {auctionMain.pending_count}
                    </span>
                    <span className="badge bg-secondary" style={{ fontSize: 16 }}>
                      Unsold: {auctionMain.unsold_count}
                    </span>
                  </div>
                  <div className="card">
                    <div className="card-header bg-secondary text-white">
                      <h5 className="mb-0">⏳ Pending Players</h5>
                    </div>
                    <div className="card-body p-0">
                      {auctionData.auction_details.pending && auctionData.auction_details.pending.length > 0 ? (
                        auctionData.auction_details.pending.map((player, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                            <div className="d-flex align-items-center">
                              <img 
                                src={player.profile_pic} 
                                alt={player.full_name}
                                className="rounded-circle me-3"
                                width="48"
                                height="48"
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder.svg';
                                }}
                              />
                              <div>
                                <div 
                                  className="fw-bold"
                                  style={{ cursor: 'pointer', color: '#0d6efd' }}
                                  onClick={() => handleOpenPlayerProfile(player)}
                                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                  {player.full_name}
                                </div>
                                <small className="text-muted">SL: {player.sl_no}</small>
                              </div>
                            </div>
                            <div className="text-end">
                              {player.auction_sold_status === '2' || player.auction_sold_status === 2 ? (
                                <span className="badge bg-danger">Unsold</span>
                              ) : null}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted py-4">
                          No pending players
                        </div>
                      )}
                    </div>
                  </div>
                </Box>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {showConfetti && soldPlayer && (
        <>
          <Confetti style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 3000 }} />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.65)',
              zIndex: 4000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={soldPlayer.img}
              alt={soldPlayer.name}
              style={{
                width: 180,
                height: 180,
                objectFit: 'cover',
                borderRadius: '50%',
                border: '6px solid #fff',
                boxShadow: '0 4px 32px rgba(0,0,0,0.25)',
                marginBottom: 32,
                background: '#f8f9fa',
              }}
              onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
            />
            <div style={{ fontSize: 48, fontWeight: 900, color: '#fff', textShadow: '2px 2px 8px #000', marginBottom: 16 }}>
              {soldPlayer.name}
            </div>
            <div style={{ fontSize: 32, color: '#ffd700', fontWeight: 700, marginBottom: 12, textShadow: '1px 1px 6px #000' }}>
              {soldPlayer.amount}
            </div>
            <div style={{ fontSize: 28, color: '#00e676', fontWeight: 600, textShadow: '1px 1px 6px #000' }}>
              {soldPlayer.team}
            </div>
          </div>
        </>
      )}
      {currentPlayer && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 2000 }}>
          <CurrentPlayerCard player={currentPlayer} />
        </div>
      )}

      {/* Search Modal */}
      <Modal
        open={searchModalOpen}
        onClose={handleCloseSearchModal}
        aria-labelledby="search-modal-title"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
            }
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{
          backgroundColor: '#ffffff',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          width: '90%',
          maxWidth: 700,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}>
          {/* Modal Header */}
          <Box sx={{
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <Typography variant="h6" component="h2" id="search-modal-title">
              🔍 Search Auction
            </Typography>
            <IconButton onClick={handleCloseSearchModal} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Input */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              autoFocus
              placeholder="Search by name, sl no, team name, or keyword (sold, unsold, pending)..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '14px',
                  opacity: 0.7
                }
              }}
            />
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="sold" size="small" onClick={() => { 
                setSearchTerm('sold'); 
                performSearch('sold');
                trackSearchInteraction('sold', null);
              }} />
              <Chip label="unsold" size="small" onClick={() => { 
                setSearchTerm('unsold'); 
                performSearch('unsold');
                trackSearchInteraction('unsold', null);
              }} />
              <Chip label="pending" size="small" onClick={() => { 
                setSearchTerm('pending'); 
                performSearch('pending');
                trackSearchInteraction('pending', null);
              }} />
            </Box>
          </Box>

          {/* Search Results */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2, backgroundColor: '#ffffff' }}>
            {/* Suggestions */}
            {suggestions.length > 0 && searchResults.type === null && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Suggestions:
                </Typography>
                <List>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid #e0e0e0',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{
                          bgcolor: suggestion.type === 'team' ? '#1976d2' : '#ff9800'
                        }}>
                          {suggestion.type === 'team' ? '👥' : '👤'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={suggestion.label}
                        secondary={suggestion.type === 'team' ? 'Team' : 'Player'}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Team Results */}
            {searchResults.type === 'team' && searchResults.data.length > 0 && (
              <Box>
                {searchResults.data.map((team, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '2px solid #1976d2',
                      backgroundColor: '#e3f2fd',
                      mb: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <img
                          src={team.team_logo && team.team_logo.trim() !== '' ? team.team_logo : '/placeholder.svg'}
                          alt={team.team_name}
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: 12,
                            border: '2px solid white'
                          }}
                          onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                        />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {team.team_name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {team.current_players_count}/{team.team_max_playes_count} Players | 
                            Max: {team.max_bid_for_a_player}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Players ({team.players ? team.players.length : 0}):
                      </Typography>
                      
                      {team.players && team.players.length > 0 ? (
                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                          {team.players.map((player, pIndex) => (
                            <ListItem
                              key={pIndex}
                              sx={{
                                backgroundColor: 'white',
                                borderRadius: 1,
                                mb: 1,
                                border: '1px solid #e0e0e0'
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  src={player.profile_pic}
                                  alt={player.full_name}
                                  onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    sx={{
                                      cursor: 'pointer',
                                      color: '#0d6efd',
                                      fontWeight: 600,
                                      '&:hover': {
                                        textDecoration: 'underline'
                                      }
                                    }}
                                    onClick={() => {
                                      handleOpenPlayerProfile(player);
                                      handleCloseSearchModal();
                                    }}
                                  >
                                    {player.full_name}
                                  </Typography>
                                }
                                secondary={`SL: ${player.sl_no} | ${player.auction_sold_amount}`}
                              />
                              <Chip label="SOLD" size="small" color="success" />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                          No players yet
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Player Results */}
            {searchResults.type === 'player' && searchResults.data.length > 0 && (
              <Box>
                {searchResults.data.map((player, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: player.status === 'sold' ? '#4caf50' : player.status === 'unsold' ? '#f44336' : '#ff9800',
                      backgroundColor: player.status === 'sold' ? '#e8f5e9' : player.status === 'unsold' ? '#ffebee' : '#fff3e0',
                      mb: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar
                        src={player.profile_pic}
                        alt={player.full_name}
                        sx={{ width: 64, height: 64, mr: 2 }}
                        onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            cursor: 'pointer',
                            color: '#0d6efd',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                          onClick={() => {
                            handleOpenPlayerProfile(player);
                            handleCloseSearchModal();
                          }}
                        >
                          {player.full_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          SL No: {player.sl_no}
                        </Typography>
                      </Box>
                      <Chip
                        label={player.status.toUpperCase()}
                        color={player.status === 'sold' ? 'success' : player.status === 'unsold' ? 'error' : 'warning'}
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    
                    {player.status === 'sold' && (
                      <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {player.team_logo && (
                            <img
                              src={player.team_logo}
                              alt={player.team_name}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                marginRight: 8
                              }}
                              onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                            />
                          )}
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Sold to: {player.team_name}
                          </Typography>
                        </Box>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                          Amount: {player.auction_sold_amount}
                        </Typography>
                      </Box>
                    )}
                    
                    {player.status === 'unsold' && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        This player was not sold in the auction
                      </Typography>
                    )}
                    
                    {player.status === 'pending' && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        This player is waiting to be auctioned
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Keyword Results (Sold/Unsold/Pending Lists) */}
            {searchResults.type === 'keyword' && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textTransform: 'capitalize' }}>
                  {searchResults.keyword} Players ({searchResults.data.length})
                </Typography>
                
                {searchResults.data.length > 0 ? (
                  <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                    {searchResults.data.map((player, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: 1,
                          mb: 1,
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={player.profile_pic}
                            alt={player.full_name}
                            onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                cursor: 'pointer',
                                color: '#0d6efd',
                                fontWeight: 600,
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => {
                                handleOpenPlayerProfile(player);
                                handleCloseSearchModal();
                              }}
                            >
                              {player.full_name}
                            </Typography>
                          }
                          secondary={
                            <>
                              SL: {player.sl_no}
                              {player.team_name && ` | Team: ${player.team_name}`}
                              {player.auction_sold_amount && ` | ${player.auction_sold_amount}`}
                            </>
                          }
                        />
                        <Chip
                          label={player.status.toUpperCase()}
                          size="small"
                          color={
                            player.status === 'sold' ? 'success' : 
                            player.status === 'unsold' ? 'error' : 
                            'warning'
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                    No {searchResults.keyword} players found
                  </Typography>
                )}
              </Box>
            )}

            {/* Not Found */}
            {searchResults.type === 'notfound' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="textSecondary">
                  No results found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Try searching with different keywords
                </Typography>
              </Box>
            )}

            {/* Initial State */}
            {!searchTerm && searchResults.type === null && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SearchIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  Start typing to search
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Search by team name, player name, sl no, or use keywords like "sold", "unsold", "pending"
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Player Profile Modal */}
      <PlayerProfileModal
        open={profileModalOpen}
        onClose={handleClosePlayerProfile}
        player={selectedPlayer}
      />

      <ToastContainer position="bottom-right" autoClose={3000} />
      {/* Footer */}
      <footer className="text-center mt-5 py-3" style={{ background: '#f8f9fa', borderTop: '1px solid #eaeaea', fontSize: 16 }}>
        © 2025 Live Cricket Kerala
        &nbsp;|&nbsp;
        <a href="https://4cr.in" style={{ color: '#0d6efd', textDecoration: 'underline' }}>
          Home
        </a>
         &nbsp;|&nbsp;
        <a href="mailto:support@4cr.in" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'underline' }}>
          Support
        </a>
      </footer>
    </div>
  );
};

export default Index;

function CurrentPlayerCard({ player }) {
  return (
    <div style={{
      width: '100%',
      background: 'rgba(255,255,255,0.98)',
      boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
      borderBottom: '4px solid #0d6efd',
      zIndex: 3000,
      position: 'relative',
      padding: '1.5rem 0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '180px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', width: '100%', maxWidth: 700 }}>
        <div style={{ flex: '0 0 auto' }}>
          <img
            src={player.img}
            alt={player.name}
            style={{
              width: 120,
              height: 120,
              objectFit: 'cover',
              borderRadius: '16px',
              border: '3px solid #0d6efd',
              background: '#f8f9fa',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#222' }}>{player.name} <span style={{ fontSize: 18, color: '#888' }}>[SL: {player.pdf_sl_no}]</span></div>
          <div style={{ fontSize: 22, color: '#0d6efd', fontWeight: 600, marginTop: 12 }}>
            Current Bid: <span style={{ color: '#198754', fontWeight: 700 }}>{player.amount}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewPlayerDisplay({ player }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <img
        src={player.img}
        alt={player.name}
        style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', background: '#f8f9fa', border: '2px solid #eee' }}
        onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
      />
      <span style={{ fontWeight: 600 }}>{player.name}</span>
      <span style={{ color: '#0d6efd', marginLeft: 8 }}>New Player</span>
    </div>
  );
}

// Helper functions for live blog events
const getEventTypeColor = (eventType) => {
  switch (eventType) {
    case 'sold':
      return '#28a745'; // Green
    case 'unsold':
      return '#dc3545'; // Red
    case 'reversed':
      return '#fd7e14'; // Orange
    case 'moved_to_pending':
      return '#17a2b8'; // Blue
    case 'auction_started':
      return '#6f42c1'; // Purple
    case 'auction_ended':
      return '#6c757d'; // Gray
    case 'bid_placed':
      return '#ffc107'; // Yellow
    default:
      return '#6c757d'; // Gray
  }
};

const getEventTypeIcon = (eventType) => {
  switch (eventType) {
    case 'sold':
      return '✓';
    case 'unsold':
      return '✗';
    case 'reversed':
      return '↺';
    case 'moved_to_pending':
      return '⏳';
    case 'auction_started':
      return '🏏';
    case 'auction_ended':
      return '🏁';
    case 'bid_placed':
      return '💰';
    default:
      return '•';
  }
};

const getPlayerRole = (playerName) => {
  // This is a simple mapping - you might want to get this from the actual player data
  const roleMap = {
    'KL Rahul': 'Batsman',
    'Virat Kohli': 'Batsman',
    'Rohit Sharma': 'Batsman',
    'MS Dhoni': 'Wicket-keeper',
    'Jasprit Bumrah': 'Bowler',
    'Ravindra Jadeja': 'All-rounder',
    'Hardik Pandya': 'All-rounder',
    'Rishabh Pant': 'Wicket-keeper',
    'Shubman Gill': 'Batsman',
    'Suryakumar Yadav': 'Batsman'
  };
  
  return roleMap[playerName] || 'Player';
};

/*
.blink {
  animation: blink-animation 1s steps(2, start) infinite;
}
@keyframes blink-animation {
  to {
    visibility: hidden;
  }
}
*/


